import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useForm} from 'react-hook-form';
import {RESOURCES as resources} from '../../resourcesProject';
import {makePostDataRequest} from '../../utils/postRequest';
import NumberInput from '../fields/NumberInput';
import FormError from './FormError';
import LoadingIndicator from '../LoadingIndicator';

/**
 * Form: Edit Project Configuration Defaults
 *
 * @return {*}
 * @constructor
 */
const FormProjectConfigDefault = (props) => {

    const projectConfig = props.project_config;

    const {register, errors, formState, handleSubmit, setValue} = useForm({mode: 'onBlur'});

    const [config, setConfig] = useState({
        max_dataset_size: projectConfig?.max_dataset_size ?? 1,
        max_shares: projectConfig?.max_shares ?? 5,
        max_volumes: projectConfig?.max_volumes ?? 3,
        projects: projectConfig?.projects ?? 3
    });
    const [successState, updateSuccessState] = useState(
        {
            message: '',
            success: false
        }
    );
    const [formStageState, setFormStageState] = useState('form');

    // Callback: Set Configuration values and update form values
    const updateConfigurations = useCallback((data) => {
        setConfig(data);
        setValue('projects', data?.projects ?? config.projects);
        setValue('max_shares', data?.max_shares ?? config.max_shares);
        setValue('max_dataset_size', data?.max_dataset_size ?? config.max_dataset_size);
        setValue('max_volumes', data?.max_volumes ?? config.max_volumes);
    }, [config, setValue]);

    // Effect: Update default Configuration values if Redux has been updated
    useEffect(() => {
        const updateConfig = () => {
            updateConfigurations(projectConfig);
        };

        if (config !== projectConfig) {
            return updateConfig();
        }

    }, [config, projectConfig, updateConfigurations]);

    // Determine if form is incomplete for disabling specific elements
    const disableSubmit = () => {
        return !!(
            !formState.isDirty ||
            formStageState === 'saving' ||
            (errors &&
                (
                    errors.projects ||
                    errors.max_shares ||
                    errors.max_dataset_size ||
                    errors.max_volumes
                ))
        );
    };

    const onConfigFormChange = () => {
        if (formStageState !== 'form') {
            setFormStageState('form');
        }
        updateSuccessState({success: false, message: ''});
    };

    // Handle submission event
    const onSubmit = (data) => {
        if (data) {
            // Assemble changed values
            let valuesToSubmit = [];
            if (parseInt(data.projects, 10) !== props.project_config.projects) {
                valuesToSubmit.push({name: 'projects', value: data.projects});
            }
            if (parseInt(data.max_shares, 10) !== props.project_config.max_shares) {
                valuesToSubmit.push({name: 'max_shares', value: data.max_shares});
            }
            if (parseInt(data.max_dataset_size, 10) !== props.project_config.max_dataset_size) {
                valuesToSubmit.push({name: 'max_dataset_size', value: data.max_dataset_size});
            }
            if (parseInt(data.max_volumes, 10) !== props.project_config.max_volumes) {
                valuesToSubmit.push({name: 'max_volumes', value: data.max_volumes});
            }

            if (valuesToSubmit.length > 0) {
                // Submit form to API
                setFormStageState('saving');
                submitForm(valuesToSubmit);
            } else {
                setFormStageState('fail');
                updateSuccessState({success: false, message: 'There are no changes to submit.'});
            }
        } else {
            setFormStageState('fail');
            updateSuccessState({success: false, message: 'There are no changes to submit.'});
        }
    };

    // Handle queued API calls and post-submission
    const submitForm = async (values) => {
        if (values.length) {
            let taskLength = values.length;
            // Start a Promise sequence with all tasks
            const starterPromise = Promise.resolve(null);
            values.reduce(
                (p, item) => p.then(() => postConfigUpdate(item)
                    .then(() => {
                        // If all Configuration updates have completed:
                        if (values.length >= (taskLength - 1)) {
                            updateSuccessState({success: true, message: ''});
                            setFormStageState('success');
                            if (typeof props.onConfigUpdated === 'function') {
                                props.onConfigUpdated();
                            }
                        }
                    })
                ), starterPromise)
                .catch((error) => {
                    console.log('Config error: ', error);
                    updateSuccessState({
                        success: false,
                        message: 'Sorry, there was an error updating a Configuration: ' + error
                    });
                    setFormStageState('fail');
                }
                );
        }
    };

    const postConfigUpdate = (item) => {
        return new Promise(function (resolve, reject) {
            if (item) {
                const onFailure = function (result) {
                    return reject('Configuration Update failed: ', result);
                };
                const onSuccess = function (result) {
                    return resolve(result);
                };
                const params = {
                    config: item.name,
                    value: item.value
                };
                const request = makePostDataRequest(resources, 'ProjectConfiguration', 'PUT', params);
                request.then(result => {
                    if (result && result.name) {
                        return onSuccess(result.data);
                    } else {
                        return resolve();
                    }
                }).catch(error => {
                    console.log('Configuration Update Error: ', error);
                    onFailure(error.response);
                });
            } else {
                return resolve();
            }
        });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="xrathus-detail">

                <div className="item-content full">
                    <div className="tab-content active">
                        <p className="instructions">
                            Set default Project Configuration values below. Values may be overridden for individual
                            users and Projects.
                        </p>

                        {/* Form Inputs */}

                        <div className="row">
                            <div className="form-group col col-xs-12 col-md-6">
                                <NumberInput
                                    id="config-projects"
                                    name="projects"
                                    label="Number of Projects"
                                    instructions={`The maximum number of Personal Projects a user may create.`}
                                    min={0}
                                    max={100}
                                    step={1}
                                    aria-describedby="error-projects-required"
                                    aria-invalid={errors.projects ? 'true' : 'false'}
                                    ref={register({
                                        required: (<FormError id="required" text="A Projects value is required"/>)
                                    })}
                                    changeFunc={() => onConfigFormChange()}
                                    className={`form-input ${errors.projects ? 'input-error' : ''}`}
                                >
                                    {errors.projects && errors.projects.message}
                                </NumberInput>
                            </div>
                            <div className="form-group col col-xs-12 col-md-6">
                                <NumberInput
                                    id="config-max-shares"
                                    name="max_shares"
                                    label="Max Number of Shares"
                                    instructions={`The maximum number of times a Project may be shared with other users.`}
                                    min={0}
                                    max={1000}
                                    step={1}
                                    aria-describedby="error-max_shares-required"
                                    aria-invalid={errors.max_shares ? 'true' : 'false'}
                                    ref={register({
                                        required: (<FormError id="required" text="A value is required"/>)
                                    })}
                                    changeFunc={() => onConfigFormChange()}
                                    className={`form-input ${errors.max_shares ? 'input-error' : ''}`}
                                >
                                    {errors.max_shares && errors.max_shares.message}
                                </NumberInput>
                            </div>
                            <div className="form-group col col-xs-12 col-md-6">
                                <NumberInput
                                    id="config-max-dataset-size"
                                    name="max_dataset_size"
                                    label="Max Dataset Size"
                                    instructions={`The maximum size (in GB) of a Dataset and its files.`}
                                    min={1}
                                    max={100}
                                    step={1}
                                    aria-describedby="error-max_dataset_size-required"
                                    aria-invalid={errors.max_dataset_size ? 'true' : 'false'}
                                    ref={register({
                                        required: (<FormError id="required" text="A value is required"/>)
                                    })}
                                    changeFunc={() => onConfigFormChange()}
                                    className={`form-input ${errors.max_dataset_size ? 'input-error' : ''}`}
                                >
                                    {errors.max_dataset_size && errors.max_dataset_size.message}
                                </NumberInput>
                            </div>
                            <div className="form-group col col-xs-12 col-md-6">
                                <NumberInput
                                    id="config-max-volumes"
                                    name="max_volumes"
                                    label="Max Number of Volumes"
                                    instructions={`The maximum number of versioned volumes a user may create per Dataset.`}
                                    min={0}
                                    max={1000}
                                    step={1}
                                    aria-describedby="error-max_volumes-required"
                                    aria-invalid={errors.max_volumes ? 'true' : 'false'}
                                    ref={register({
                                        required: (<FormError id="required" text="A value is required"/>)
                                    })}
                                    changeFunc={() => onConfigFormChange()}
                                    className={`form-input ${errors.max_volumes ? 'input-error' : ''}`}
                                >
                                    {errors.max_volumes && errors.max_volumes.message}
                                </NumberInput>
                            </div>
                        </div>
                    </div>

                    {/* Messaging and Actions */}

                    <div className="form-btns right">
                        {formStageState === 'saving' &&
                        <>
                            <LoadingIndicator color="dark" centered={false} active={true}/>
                            <p className="message">Saving Configuration...</p>
                        </>
                        }
                        {formStageState === 'success' &&
                        <p className="message">Global Project Configuration saved</p>
                        }
                        {formStageState === 'fail' &&
                        <>
                            {successState.message !== '' &&
                            <p className="message">
                                {successState.message}
                            </p>
                            }
                            {successState.message === '' &&
                            <p className="message">
                                Sorry, there was an issue saving the Global Project Configuration.
                            </p>
                            }
                        </>
                        }

                        <button type="submit" className="btn btn-primary" disabled={!!disableSubmit()}>
                            Save Configuration
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
};

const mapStateToProps = (state) => {
    return {
        project_config: state.project_config
    };
};

FormProjectConfigDefault.propTypes = {
    project_config: PropTypes.any,
    onConfigUpdated: PropTypes.any,
};

export default connect(
    mapStateToProps
)(FormProjectConfigDefault);